import { OPERATOR_FLOW } from '../operator/operatorConstants';
import { isRetryable } from '../operator/operatorErrors';
import { earlierVisitFailure, offScreenFailure, offScreenSignal } from '../operator/operatorTaskScope';
import { reportShortAtStop, scanRouteCode, takeAtStop, undoLastPick } from './operatorPickingRouteThunks';
import { formatQuantity } from '../../utils/inventoryLabels';
import { shortMessage, takeMessage, undoMessage } from './operatorPickingRouteUtils';

/** A band's second button, by what it does (see `workspaces/operator/operatorRetry`). */
export const BAND_ACTION = {
  ROUTE_UNDO: 'routeUndo',
};

/** «Deshacer» of a take, on its band. */
const undoAction = (routeId, pickKey) => ({
  kind: BAND_ACTION.ROUTE_UNDO,
  label: 'Deshacer',
  busyLabel: 'Deshaciendo…',
  testId: 'route-undo',
  routeId,
  pickKey,
});

/**
 * A write's band. Off screen it names the route and says no step to take;
 * a lost answer offers «Reintentar» wherever it shows: the band carries the
 * write, and its key keeps it from registering twice. On screen, a take
 * offers «Deshacer».
 */
const write = (message, { undoable = false } = {}) => ({
  success: ({ payload, meta }) => {
    const name = meta.offScreenTask;
    const signal = message(payload, meta.arg, meta.stopHint);
    if (name) return offScreenSignal(name, signal);
    return undoable && payload.pick_key && !meta.earlierVisit
      ? { ...signal, action: undoAction(meta.arg.routeId, payload.pick_key) }
      : signal;
  },
  error: ({ payload, meta }, fallback) => {
    const name = meta.offScreenTask;
    if (meta.earlierVisit) return earlierVisitFailure(payload);
    if (!name) return undefined;
    return isRetryable(payload) ? offScreenSignal(name, fallback) : offScreenFailure(name, payload);
  },
  retry: OPERATOR_FLOW.PICKING,
  keyed: true,
});

/** What the band says for each outcome of the route; see `operatorListeners`. */
export const PICKING_ROUTE_SIGNAL_RULES = {
  [scanRouteCode.typePrefix]: {
    success: ({ payload }) => {
      if (payload.kind === 'slot') {
        return {
          tone: 'step',
          message: payload.again
            ? `Ya estás en ${payload.slotCode}. Escanea los productos.`
            : `En ${payload.slotCode}. Escanea los productos.`,
        };
      }
      const count = `${formatQuantity(payload.counted)} de ${formatQuantity(payload.pending)}`;
      return {
        tone: 'step',
        message: payload.factor > 1 ? `Caja ×${payload.factor} de ${payload.sku} · ${count}` : `${payload.sku} · ${count}`,
      };
    },
  },
  [takeAtStop.typePrefix]: write(takeMessage, { undoable: true }),
  [reportShortAtStop.typePrefix]: write(shortMessage),
  [undoLastPick.typePrefix]: write(undoMessage),
};
